import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { getToken } from '../../utils/auth';
import {
  XMarkIcon,
  ArrowPathIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import debounce from 'lodash/debounce';

const { VITE_API_SERVICE } = import.meta.env;

async function apiCall(endpoint, options = {}) {
  const token = getToken();
  const response = await fetch(`${VITE_API_SERVICE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    }
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(
      data.message || `Failed to ${options.method || 'fetch'} data`
    );
  }
  return data;
}

async function fetchUserData(userId) {
  const data = await apiCall('/v1/users/get', {
    method: 'POST',
    body: JSON.stringify({ userId })
  });
  return data.user;
}

async function fetchRegions() {
  return apiCall('/v1/regions/list');
}

async function updateUserExcludedRegions(userId, excludedRegions) {
  return apiCall('/v1/users/update', {
    method: 'POST',
    body: JSON.stringify({ userId, excludedRegions })
  });
}

function normalizeRegion(region, allRegions) {
  const id = region.id || region;
  if (typeof region === 'number' || typeof region === 'string') {
    const found = allRegions.regions.find((r) => r.id === id);
    return found ? { id, name: found.name } : null;
  }
  return { id, name: region.name };
}

export default function RegionExclusion() {
  const { userId } = useParams();

  // State
  const [regionSearch, setRegionSearch] = useState('');
  const [selectedRegions, setSelectedRegions] = useState(new Map());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedExpanded, setSelectedExpanded] = useState(true);
  const [availableExpanded, setAvailableExpanded] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Queries
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      return fetchUserData(userId);
    },
    enabled: !!userId,
    retry: 2,
    staleTime: 30000
  });

  const {
    data: allRegions,
    isLoading: isLoadingAllRegions,
    error: allRegionsError,
    refetch: refetchAllRegions
  } = useQuery({
    queryKey: ['all-regions'],
    queryFn: async () => fetchRegions(),
    staleTime: 60000,
    retry: 2
  });

  // Mutations
  const updateRegionsMutation = useMutation({
    mutationFn: (newRegions) => {
      const regionData = Array.from(newRegions.values());
      return updateUserExcludedRegions(userId, regionData);
    },
    onSuccess: async () => {
      setHasUnsavedChanges(false);
      await Promise.all([refetchUser(), refetchAllRegions()]);
    }
  });

  // Effects
  useEffect(() => {
    if (userData?.excludedRegions && allRegions?.regions) {
      initializeSelectedRegions(userData, allRegions);
    }
  }, [userData, allRegions]);

  useEffect(() => {
    setHasUnsavedChanges(false);
  }, [userId]);

  function initializeSelectedRegions(userData, allRegions) {
    const regionMap = new Map();
    const regionObjects = Array.isArray(userData.excludedRegions)
      ? userData.excludedRegions
      : allRegions.regions.filter((r) =>
          userData.excludedRegions?.includes(r.id)
        );

    regionObjects.forEach((region) => {
      const normalized = normalizeRegion(region, allRegions);
      if (normalized) {
        regionMap.set(normalized.id, normalized);
      }
    });
    setSelectedRegions(regionMap);
  }

  function getFilteredRegions() {
    const filteredRegions = allRegions?.regions || [];
    return regionSearch
      ? filteredRegions.filter((region) =>
          region?.name?.toLowerCase().includes(regionSearch.toLowerCase())
        )
      : filteredRegions;
  }

  function splitRegions(regions) {
    const selectedIds = new Set(Array.from(selectedRegions.keys()));
    return {
      selected: regions.filter((region) => selectedIds.has(region.id)),
      available: regions.filter((region) => !selectedIds.has(region.id))
    };
  }

  function handleRegionChange(region) {
    const normalized = normalizeRegion(region, { regions: [region] });
    if (!normalized) return;

    setSelectedRegions((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(normalized.id)) {
        newMap.delete(normalized.id);
      } else {
        newMap.set(normalized.id, normalized);
      }
      return newMap;
    });
    setHasUnsavedChanges(true);
  }

  function handleSave() {
    setShowSaveModal(true);
  }

  function handleConfirmSave() {
    updateRegionsMutation.mutate(selectedRegions, {
      onSuccess: () => {
        setShowSaveModal(false);
      }
    });
  }

  const debouncedSetSearch = debounce((value) => setRegionSearch(value), 300);

  const renderSearchBox = () => (
    <div className="form-control mb-4">
      <div className="relative">
        <input
          type="text"
          placeholder="Search regions..."
          className="input input-bordered input-sm w-full pr-10"
          value={regionSearch}
          onChange={(e) => {
            setRegionSearch(e.target.value);
            debouncedSetSearch(e.target.value);
          }}
        />
        {regionSearch && (
          <button
            className="btn btn-ghost btn-xs absolute right-2 top-1/2 -translate-y-1/2"
            onClick={() => setRegionSearch('')}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  const renderRegionList = (regions, isSelected) => (
    <div className="space-y-1 mt-2">
      {regions.map((region) => (
        <label
          key={region.id}
          className={`flex items-center p-2 gap-3 rounded-lg ${
            isSelected ? 'bg-base-200 hover:bg-base-300' : 'hover:bg-base-200'
          } cursor-pointer transition-colors`}
        >
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={isSelected}
            onChange={() => handleRegionChange(region)}
          />
          <span className="text-sm">{region.name}</span>
        </label>
      ))}
    </div>
  );

  const renderRegionSection = (
    title,
    regions,
    isExpanded,
    setExpanded,
    isSelected
  ) => (
    <div>
      <button
        onClick={() => setExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-2 hover:bg-base-200 rounded-lg transition-colors"
      >
        <h3 className="text-sm font-medium opacity-70">
          {title} ({regions.length})
        </h3>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
        />
      </button>
      {isExpanded && renderRegionList(regions, isSelected)}
    </div>
  );

  // Main Render
  const isLoading = isLoadingUser || isLoadingAllRegions;
  const error = userError || allRegionsError;
  const regions = getFilteredRegions();
  const { selected, available } = splitRegions(regions);

  return (
    <div className="card bg-white border border-base-200">
      <div className="card-body">
        <div className="flex justify-between items-center mb-4">
          <h2 className="card-title text-lg">Region Exclusion</h2>
          {hasUnsavedChanges && (
            <div className="flex items-center gap-2">
              <span className="badge badge-warning badge-sm">Unsaved</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleSave}
                disabled={updateRegionsMutation.isPending}
              >
                {updateRegionsMutation.isPending ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          )}
        </div>

        {userId ? (
          <>
            <div className="text-sm font-medium mb-4">
              Excluding regions for:{' '}
              <span className="text-base-content">{userData?.email}</span>
            </div>
            {renderSearchBox()}
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-40">
                  <span className="loading loading-spinner loading-md"></span>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-sm text-error mb-2">
                    {(userError || allRegionsError).message}
                  </p>
                  <button
                    onClick={() => {
                      refetchUser();
                      refetchAllRegions();
                    }}
                    className="btn btn-ghost btn-xs gap-2"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  {selected.length > 0 &&
                    renderRegionSection(
                      'Selected Regions',
                      selected,
                      selectedExpanded,
                      setSelectedExpanded,
                      true
                    )}
                  {available.length > 0 &&
                    renderRegionSection(
                      'Available Regions',
                      available,
                      availableExpanded,
                      setAvailableExpanded,
                      false
                    )}
                  {regions.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-sm opacity-60">
                        {regionSearch
                          ? `No regions found matching "${regionSearch}"`
                          : 'No regions available'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : (
          <div className="text-sm opacity-60 text-center py-4">
            Select a user to manage excluded regions
          </div>
        )}
      </div>

      {/* Save Confirmation Modal */}
      {showSaveModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Confirm Save Changes</h3>
            <p className="mb-4">
              Are you sure you want to save the changes to the excluded regions?
            </p>
            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmSave}
                disabled={updateRegionsMutation.isPending}
              >
                {updateRegionsMutation.isPending ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
